// src/services/unleashedSalesOrderCsv.ts
// -------------------------------------
// Pure builder that turns staged Sales Orders (`request_sales_orders`, grouped into
// SalesOrder[] by src/data/salesOrders) into a CSV in Unleashed's Sales Order import
// format — one row per order LINE, header fields repeated on every line (the way the
// Unleashed template and our flat table both work).
//
// Purpose: let an admin export the SOs the portal generated and MANUALLY import them
// into Unleashed to validate them, before the API push worker is switched on.
//
// ⚠️ HEADER STRINGS ARE BEST-EFFORT and MUST be confirmed against a real Unleashed
// Sales Order CSV import template before go-live (a wrong header silently breaks the
// import). They are all defined in the single ordered COLUMNS list below, so
// correcting them is a one-place edit. The golden-file test pins the current output.

import type { SalesOrder } from "../data/salesOrders";
import { escapeCsvField } from "../utils/csv";

type Line = SalesOrder["lines"][number];

// Format a numeric money value to 2dp; blank for null/NaN.
const money = (v: number | null): string => {
  if (v == null) return "";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

const str = (v: unknown): string => (v == null ? "" : String(v));

// Unleashed's SO import template dates are DD/MM/YYYY. Our stored dates are ISO
// (YYYY-MM-DD, possibly with a time part) — reformat those; pass anything else through.
const ddmmyyyy = (v: string | null): string => {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
};

// One column = a header + a per-line accessor. The order AND header text MUST match
// the Unleashed Sales Order CSV import template EXACTLY — locked against a real
// Unleashed export (SalesOrder template, 14 Jul 2026). `*` marks Unleashed's required
// columns. If Unleashed changes its template, update this list (and the golden test).
interface Column {
  header: string;
  value: (line: Line) => string;
}

export const COLUMNS: Column[] = [
  { header: "*Order Number", value: (l) => str(l.order_number) },
  { header: "*Customer Code", value: (l) => str(l.customer_code) },
  { header: "Tax Code", value: (l) => str(l.tax_code) },
  { header: "Tax Rate", value: (l) => str(l.tax_rate) },
  { header: "Exchange Rate", value: (l) => str(l.exchange_rate ?? 1) },
  { header: "Customer Reference", value: (l) => str(l.customer_reference) },
  { header: "Comments", value: (l) => str(l.comments) },
  { header: "Order Date (DD/MM/YYYY)", value: (l) => ddmmyyyy(l.order_date) },
  { header: "Quote Expiry Date (DD/MM/YYYY)", value: (l) => ddmmyyyy(l.quote_expiry_date) },
  { header: "Required Date (DD/MM/YYYY)", value: (l) => ddmmyyyy(l.required_date) },
  { header: "Warehouse Code", value: (l) => str(l.warehouse_code) },
  { header: "Delivery Contact", value: (l) => str(l.delivery_contact) },
  { header: "Delivery Name", value: (l) => str(l.delivery_name) },
  { header: "Delivery Street Address", value: (l) => str(l.delivery_street_address) },
  { header: "Delivery Street Address 2", value: (l) => str(l.delivery_street_address_2) },
  { header: "Delivery Suburb", value: (l) => str(l.delivery_suburb) },
  { header: "Delivery City", value: (l) => str(l.delivery_city) },
  { header: "Delivery Region", value: (l) => str(l.delivery_region) },
  { header: "Delivery Post Code", value: (l) => str(l.delivery_post_code) },
  { header: "Delivery Country", value: (l) => str(l.delivery_country) },
  { header: "Delivery Instruction", value: (l) => str(l.delivery_instruction) },
  { header: "Discount (%)", value: (l) => str(l.discount_percent) },
  { header: "Sales Order Group", value: (l) => str(l.sales_order_group) },
  { header: "Sales Person Email", value: (l) => str(l.sales_person_email) },
  { header: "*Line Number", value: (l) => str(l.line_number) },
  { header: "*Product Code", value: (l) => str(l.product_code) },
  { header: "*Order Quantity", value: (l) => str(l.order_quantity) },
  { header: "Line Discount (%)", value: (l) => str(l.line_discount_percent) },
  { header: "Unit Price", value: (l) => money(l.unit_price) },
  { header: "Line Comments", value: (l) => str(l.line_comments) },
];

// RFC-4180-style field escaping now lives in src/utils/csv.ts (shared with the
// Reports export, 5 Oct 2026). Re-exported here unchanged so existing importers and
// tests keep working.
export { escapeCsvField };

const CRLF = "\r\n";

/**
 * Build the Unleashed Sales Order import CSV for the given orders. One header row,
 * then one row per order line (ordered by the incoming orders, then line_number).
 * Returns just the header row when there are no orders.
 */
export function toUnleashedCsv(orders: SalesOrder[]): string {
  const rows: string[] = [];
  rows.push(COLUMNS.map((c) => escapeCsvField(c.header)).join(","));
  for (const order of orders) {
    for (const line of order.lines) {
      rows.push(COLUMNS.map((c) => escapeCsvField(c.value(line))).join(","));
    }
  }
  return rows.join(CRLF);
}

// A stable, human-friendly filename for the download, e.g.
// "unleashed-sales-orders-2026-07-14.csv".
export function csvFilename(date = new Date()): string {
  const iso = date.toISOString().slice(0, 10);
  return `unleashed-sales-orders-${iso}.csv`;
}
